import { formatLocaleCurrency, formatPlaceOfInsurance } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { ClientsListView } from "../../../../../../client/components/ClientsListView";
import { ClientFormType } from "../../../../../../client/enums";
import { Client } from "../../../../../../client/types";
import { RealtyInsurance } from "../../../../../types";

const LABEL_WIDTH = 200;

type RealtyInsuranceAmountProps = {
  data: {
    insuranceAmount: number;
    liabilityInsuranceAmount?: number;
  };
};

const RealtyInsuranceAmount = ({ data }: RealtyInsuranceAmountProps) => {
  const { t } = useTranslation();

  return (
    <Descriptions
      size="small"
      bordered
      column={1}
      styles={{ label: { width: LABEL_WIDTH } }}
      items={[
        {
          label: t("contract.attrs.insurances.insuranceData.realty.insuranceAmount"),
          children: formatLocaleCurrency(data.insuranceAmount, 0),
        },
        {
          label: t("contract.attrs.insurances.insuranceData.realty.liabilityInsuranceAmount"),
          children: isDefined(data.liabilityInsuranceAmount)
            ? formatLocaleCurrency(data.liabilityInsuranceAmount, 0)
            : undefined,
        },
      ]}
    />
  );
};

interface Props {
  insurance: RealtyInsurance;
  insuredClient?: Client;
  vinculationClient?: Client;
}

const RealtyInsuranceView = ({ insurance, insuredClient, vinculationClient }: Props) => {
  const { t } = useTranslation();

  const { insuranceData } = insurance;

  return (
    <>
      <Descriptions
        size="small"
        bordered
        column={3}
        items={[
          {
            label: t("calc.realty.enums.buildingState._label"),
            children: t("calc.realty.enums.buildingState." + insuranceData.buildingState),
          },
          {
            label: t("contract.attrs.insurances.insuranceData.constructionYear"),
            children: insuranceData.constructionYear,
            span: 2,
          },
          {
            label: t("contract.attrs.insurances.insuranceData.permanentlyOccupied"),
            children: tBoolean(insuranceData.permanentlyOccupied),
          },
          {
            label: t("contract.attrs.insurances.insuranceData.complicity"),
            children: tBoolean(insuranceData.complicity),
          },
          {
            label: t("contract.attrs.insurances.insuranceData.vinculation"),
            children: tBoolean(insuranceData.vinculation),
          },
          {
            label: t("contract.attrs.insurances.insuranceData.placeOfInsurance._label"),
            children: formatPlaceOfInsurance(insuranceData.placeOfInsurance),
            span: 3,
          },
          {
            label: t("calc.realty.enums.buildingType._label"),
            children: (
              <Descriptions
                bordered
                size="small"
                title={t("calc.realty.enums.buildingType." + insuranceData.buildingType)}
                styles={{ header: { marginBottom: 8 }, label: { width: LABEL_WIDTH } }}
                column={1}
                items={
                  insuranceData.houseOrRecreationalBuilding
                    ? [
                        {
                          label: t("calc.realty.enums.constructionMaterial._label"),
                          children: t(
                            "calc.realty.enums.constructionMaterial." +
                              insuranceData.houseOrRecreationalBuilding.constructionMaterial,
                          ),
                        },
                        {
                          label: t("calc.realty.enums.roofType._label"),
                          children: t(
                            "calc.realty.enums.roofType." + insuranceData.houseOrRecreationalBuilding.roofType,
                          ),
                        },
                        {
                          label: t("contract.attrs.insurances.insuranceData.houseOrRecreationalBuilding.buildArea"),
                          children: (
                            <span>
                              {insuranceData.houseOrRecreationalBuilding.buildArea} m<sup>2</sup>
                            </span>
                          ),
                        },
                      ]
                    : insuranceData.apartment
                      ? [
                          {
                            label: t("contract.attrs.insurances.insuranceData.apartment.floor"),
                            children: insuranceData.apartment.floor,
                          },
                          {
                            label: t("contract.attrs.insurances.insuranceData.apartment.floorArea"),
                            children: (
                              <span>
                                {insuranceData.apartment.floorArea} m<sup>2</sup>
                              </span>
                            ),
                          },
                        ]
                      : undefined
                }
              />
            ),
            span: 3,
          },

          {
            label: t("contract.attrs.insurances.insuranceData.realty._label"),
            children: insuranceData.realty ? (
              <RealtyInsuranceAmount
                data={{
                  insuranceAmount: insuranceData.realty.insuranceAmount,
                  liabilityInsuranceAmount: insuranceData.realty.liabilityInsuranceAmount,
                }}
              />
            ) : (
              tBoolean(!!insuranceData.realty)
            ),
            span: 3,
          },
          {
            label: t("contract.attrs.insurances.insuranceData.household._label"),
            children: insuranceData.household ? (
              <RealtyInsuranceAmount
                data={{
                  insuranceAmount: insuranceData.household.insuranceAmount,
                  liabilityInsuranceAmount: insuranceData.household.liabilityInsuranceAmount,
                }}
              />
            ) : (
              tBoolean(!!insuranceData.household)
            ),
            span: 3,
          },
          {
            label: t("contract.attrs.insurances.insuranceData.reinsurances._label"),
            children: (
              <Descriptions
                size="small"
                bordered
                column={2}
                styles={{ label: { width: LABEL_WIDTH } }}
                items={[
                  {
                    label: t("contract.attrs.insurances.insuranceData.reinsurances.cyberSecurity"),
                    children: tBoolean(insuranceData.reinsurances.cyberSecurity),
                  },
                  {
                    label: t("contract.attrs.insurances.insuranceData.reinsurances.warranty"),
                    children: tBoolean(insuranceData.reinsurances.warranty),
                  },
                  {
                    label: t("contract.attrs.insurances.insuranceData.reinsurances.cycling"),
                    children: tBoolean(insuranceData.reinsurances.cycling),
                  },
                  {
                    label: t("contract.attrs.insurances.insuranceData.reinsurances.loan"),
                    children: tBoolean(insuranceData.reinsurances.loan),
                  },
                  {
                    label: t("contract.attrs.insurances.insuranceData.reinsurances.rent"),
                    children: tBoolean(insuranceData.reinsurances.rent),
                  },
                  {
                    label: t("contract.attrs.insurances.insuranceData.reinsurances.allRisk"),
                    children: tBoolean(insuranceData.reinsurances.allRisk),
                  },
                ]}
              />
            ),
            span: 3,
          },
        ]}
      />

      {insuredClient && (
        <ClientsListView
          className="margin-top-medium"
          clientsData={[
            {
              client: insuredClient,
              clientFormTypes: [ClientFormType.INSURED],
            },
            ...(vinculationClient
              ? [
                  {
                    client: vinculationClient,
                    clientFormTypes: [ClientFormType.VINCULATION],
                  },
                ]
              : []),
          ]}
        />
      )}
    </>
  );
};

export default RealtyInsuranceView;
